import React, { useState, useRef, useLayoutEffect } from "react";

import { SingleSelectableLabel } from "./components/SingleSelectableLabel/SingleSelectableLabel";

export default function SelectableLabelsExpandable({
  feed,
  setValue,
  translative,
  selectDensity,
  disabled,
  isDataFilterNameFromValue,
  getCustomLabel,
}) {
  const containerRef = useRef(null);

  const [visibleCount, setVisibleCount] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Reset measurement when feed changes
  useLayoutEffect(() => {
    setVisibleCount(null);
    setExpanded(false);
  }, [feed]);

  useLayoutEffect(() => {
    const measureRows = () => {
      const container = containerRef.current;
      if (!container) return;

      // Only measure if we're showing the full feed (expanded or initial state)
      if (visibleCount !== null && !expanded) return;

      const labelChildren = Array.from(container.children).filter(
        (child) => !child.dataset.more,
      );

      if (!labelChildren.length) {
        setHasOverflow(false);
        setVisibleCount(0);
        return;
      }

      const rows = [];

      for (const child of labelChildren) {
        const top = child.offsetTop;
        const lastRow = rows[rows.length - 1];

        if (!lastRow || lastRow.top !== top) {
          rows.push({ top, items: [child] });
        } else {
          lastRow.items.push(child);
        }

        if (rows.length === 3) {
          break;
        }
      }

      if (rows.length <= 2) {
        setHasOverflow(false);
        setVisibleCount(labelChildren.length);
        return;
      }

      const rowGap = parseFloat(
        getComputedStyle(container).getPropertyValue("gap") || "0",
      );

      const secondRowItems = rows[1].items.slice();
      const computeRowWidth = (items) => {
        if (!items.length) return 0;
        const itemsWidth = items.reduce(
          (sum, item) => sum + item.getBoundingClientRect().width,
          0,
        );
        const gaps = rowGap * Math.max(items.length - 1, 0);
        return itemsWidth + gaps;
      };

      let truncatedSecondRow = secondRowItems;
      let secondRowWidth = computeRowWidth(truncatedSecondRow);

      while (secondRowWidth > 400 && truncatedSecondRow.length > 0) {
        truncatedSecondRow = truncatedSecondRow.slice(
          0,
          truncatedSecondRow.length - 1,
        );
        secondRowWidth = computeRowWidth(truncatedSecondRow);
      }

      const firstRowCount = rows[0].items.length;

      setHasOverflow(true);
      setVisibleCount(firstRowCount + truncatedSecondRow.length);
    };

    measureRows();
  }, [feed, expanded, visibleCount]);

  const hiddenCount =
    visibleCount !== null ? (feed?.length ?? 0) - visibleCount : 0;

  const displayedFeed =
    expanded || visibleCount === null
      ? feed
      : (feed ?? []).slice(0, visibleCount);

  return (
    <div ref={containerRef} className="select-tags__selectable-labels">
      {displayedFeed?.map((f, index) => (
        <SingleSelectableLabel
          key={index}
          value={f}
          onClick={setValue}
          translative={translative}
          selectDensity={selectDensity}
          disabled={disabled}
          isDataFilterNameFromValue={isDataFilterNameFromValue}
          getCustomLabel={getCustomLabel}
          className="select-tags__selectable-labels__single--expandable"
        />
      ))}

      {!expanded && hasOverflow && hiddenCount > 0 && (
        <div
          data-more
          className="select-tags__more text-h7-regular clickable"
          onClick={() => {
            setExpanded(true);
          }}
        >
          +{hiddenCount}
        </div>
      )}

      {expanded && hasOverflow && displayedFeed?.length > 0 && (
        <div
          data-more
          className="select-tags__more text-h7-regular clickable"
          onClick={() => {
            setExpanded(false);
          }}
        >
          -
        </div>
      )}
    </div>
  );
}
